import React, { useEffect, useState } from 'react';
import {
  View, Text, Switch, ScrollView, ActivityIndicator, StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const C = {
  primary: '#21808D', bg: '#f6f8fb', card: '#ffffff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
  sky: '#0ea5e9', amber: '#d97706',
};

const MOODS = [
  { id: 'sunny', label: 'Sunny' },
  { id: 'cloudy', label: 'Cloudy' },
  { id: 'rainy', label: 'Rainy' },
  { id: 'night', label: 'Night' },
  { id: 'morning', label: 'Morning' },
  { id: 'evening', label: 'Evening' },
];

type Props = { branchId?: string };

export default function Settings({ branchId = '' }: Props) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>({});
  const [weatherLive, setWeatherLive] = useState(true);

  // Global app settings (weather kill-switch).
  useEffect(() => {
    const ref = doc(db, 'zapee_app_settings', 'general');
    return onSnapshot(ref, (snap) => {
      const d = (snap.data() as any) || {};
      setWeatherLive((d.weatherLiveEnabled ?? true) !== false);
    });
  }, []);

  // Branch settings doc.
  useEffect(() => {
    if (!branchId) { setLoading(false); return; }
    const ref = doc(db, 'zapee_branches', branchId);
    return onSnapshot(ref, (snap) => {
      setData((snap.data() as any) || {});
      setLoading(false);
    }, () => setLoading(false));
  }, [branchId]);

  const write = (patch: any) =>
    setDoc(doc(db, 'zapee_branches', branchId), patch, { merge: true });
  const writeWeatherLive = (v: boolean) =>
    setDoc(doc(db, 'zapee_app_settings', 'general'),
      { weatherLiveEnabled: v }, { merge: true });

  if (loading) {
    return <View style={styles.center}><ActivityIndicator color={C.primary} /></View>;
  }
  if (!branchId) {
    return (
      <View style={styles.center}>
        <Text style={{ color: C.textMuted }}>
          No branch assigned to this shareholder profile.
        </Text>
      </View>
    );
  }

  const num = (k: string, d = 0) => Number(data[k] ?? d);
  const dcfg = data.deliveryConfig || {};

  return (
    <ScrollView style={{ flex: 1, backgroundColor: C.bg }}
      contentContainerStyle={{ padding: 24, maxWidth: 760 }}>
      <Text style={styles.h1}>Settings</Text>

      {/* STATUS */}
      <Section title="STATUS">
        <SwitchRow label="Branch active" sub="Inactive branches are hidden from customers"
          val={data.active ?? true} onVal={(v) => write({ active: v })} />
        <Divider />
        <SwitchRow label="Accepting orders" sub="Turn off to pause new orders"
          val={data.online ?? false} onVal={(v) => write({ online: v })} />
      </Section>

      {/* WEATHER */}
      <Section title="WEATHER">
        <SwitchRow label="Use live weather API"
          sub={weatherLive ? 'Weather follows real conditions (Google)' : 'Weather follows the clock — no API'}
          val={weatherLive} onVal={writeWeatherLive} />
        <Divider />
        <SwitchRow label="Show weather backdrop" sub="Animated sky behind the storefront"
          val={data.weatherEnabled ?? true} onVal={(v) => write({ weatherEnabled: v })} />
        <Divider />
        <View style={{ paddingVertical: 12 }}>
          <Text style={styles.rowLabel}>Weather mood</Text>
          <View style={styles.chips}>
            {MOODS.map((m) => {
              const on = (data.weather ?? 'sunny') === m.id;
              return (
                <TouchableOpacity key={m.id} onPress={() => write({ weather: m.id })}
                  style={[styles.chip, on && styles.chipOn]}>
                  <Text style={[styles.chipTxt, on && styles.chipTxtOn]}>{m.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </Section>

      {/* BANNERS */}
      <Section title="BANNERS">
        <SwitchRow label="Show banners" val={data.bannersEnabled ?? true}
          onVal={(v) => write({ bannersEnabled: v })} />
        <Divider />
        <SwitchRow label="Auto-cycle" val={data.bannerAutoCycle ?? true}
          onVal={(v) => write({ bannerAutoCycle: v })} />
        <Divider />
        <SwitchRow label="Show dot indicators" val={data.bannerShowDots ?? true}
          onVal={(v) => write({ bannerShowDots: v })} />
        <Divider />
        <Stepper label="Cycle interval" unit="s" min={2} max={15} step={1}
          value={num('bannerCycleSeconds', 4)} onChange={(v) => write({ bannerCycleSeconds: v })} />
        <Divider />
        <Stepper label="Weather → banner delay" unit="s" min={0} max={30} step={1}
          value={num('weatherSwapDelaySeconds', 5)} onChange={(v) => write({ weatherSwapDelaySeconds: v })} />
        <Text style={styles.note}>Banner images are managed in the Branch screen.</Text>
      </Section>

      {/* SURCHARGES */}
      <Section title="SURCHARGES">
        <Stepper label="Surge amount" unit="₹" prefix min={0} max={200} step={5}
          value={num('surgeAmount')} onChange={(v) => write({ surgeAmount: v })} />
        <Divider />
        <Stepper label="Platform fee" unit="₹" prefix min={0} max={100} step={5}
          value={num('platformFee')} onChange={(v) => write({ platformFee: v })} />
      </Section>

      {/* RADIUS & DELIVERY */}
      <Section title="SERVICE RADIUS & DELIVERY">
        <Stepper label="Food radius" unit="km" min={0} max={30} step={1}
          value={num('foodRadiusKm', num('radiusKm'))} onChange={(v) => write({ foodRadiusKm: v })} />
        <Divider />
        <Stepper label="Legacy service radius" unit="km" min={0} max={30} step={1}
          value={num('radiusKm')} onChange={(v) => write({ radiusKm: v })} />
        <Divider />
        <Stepper label="Min delivery time" unit="min" min={0} max={90} step={5}
          value={Number(dcfg.minDeliveryTime ?? 0)}
          onChange={(v) => write({ deliveryConfig: { minDeliveryTime: v } })} />
        <Divider />
        <Stepper label="Max delivery time" unit="min" min={0} max={120} step={5}
          value={Number(dcfg.maxDeliveryTime ?? 0)}
          onChange={(v) => write({ deliveryConfig: { maxDeliveryTime: v } })} />
      </Section>

      <View style={{ height: 30 }} />
    </ScrollView>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={{ marginBottom: 18 }}>
      <Text style={styles.section}>{title}</Text>
      <View style={styles.card}>{children}</View>
    </View>
  );
}

const Divider = () => <View style={styles.divider} />;

function SwitchRow({ label, sub, val, onVal }:
  { label: string; sub?: string; val: boolean; onVal: (v: boolean) => void }) {
  return (
    <View style={styles.row}>
      <View style={{ flex: 1, paddingRight: 12 }}>
        <Text style={styles.rowLabel}>{label}</Text>
        {!!sub && <Text style={styles.rowSub}>{sub}</Text>}
      </View>
      <Switch value={val} onValueChange={onVal}
        trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
    </View>
  );
}

function Stepper({ label, value, min, max, step, unit, prefix, onChange }:
  { label: string; value: number; min: number; max: number; step: number; unit?: string; prefix?: boolean; onChange: (v: number) => void }) {
  const clamp = (v: number) => Math.max(min, Math.min(max, v));
  const display = prefix ? `${unit ?? ''}${value}` : `${value}${unit ? ` ${unit}` : ''}`;
  return (
    <View style={styles.row}>
      <Text style={[styles.rowLabel, { flex: 1 }]}>{label}</Text>
      <View style={styles.stepper}>
        <TouchableOpacity style={styles.stepBtn} onPress={() => onChange(clamp(value - step))}>
          <Text style={styles.stepSign}>−</Text>
        </TouchableOpacity>
        <Text style={styles.stepVal}>{display}</Text>
        <TouchableOpacity style={styles.stepBtn} onPress={() => onChange(clamp(value + step))}>
          <Text style={styles.stepSign}>+</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: C.bg },
  h1: { fontSize: 22, fontWeight: '800', color: C.textPrimary, marginBottom: 18 },
  section: { fontSize: 11, fontWeight: '800', color: C.textMuted, letterSpacing: 0.8, marginBottom: 8 },
  card: { backgroundColor: C.card, borderRadius: 14, borderWidth: 1, borderColor: C.border, paddingHorizontal: 16 },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 13 },
  rowLabel: { fontSize: 14.5, fontWeight: '700', color: C.textPrimary },
  rowSub: { fontSize: 12, color: C.textMuted, marginTop: 3 },
  divider: { height: 1, backgroundColor: C.border },
  chips: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 10 },
  chip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20, borderWidth: 1, borderColor: C.border, marginRight: 8, marginBottom: 8 },
  chipOn: { backgroundColor: C.sky, borderColor: C.sky },
  chipTxt: { fontSize: 13, fontWeight: '600', color: C.textMuted },
  chipTxtOn: { color: '#fff' },
  stepper: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: C.border, borderRadius: 10, overflow: 'hidden' },
  stepBtn: { paddingHorizontal: 14, paddingVertical: 7 },
  stepSign: { fontSize: 18, fontWeight: '800', color: C.primary },
  stepVal: { minWidth: 64, textAlign: 'center', fontSize: 14, fontWeight: '800', color: C.textPrimary },
  note: { fontSize: 12, color: C.textMuted, paddingVertical: 12 },
});
